
import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { UserWithMembership } from '@/hooks/useUsers';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';

interface UserDeleteDialogProps {
  user: UserWithMembership | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

const UserDeleteDialog: React.FC<UserDeleteDialogProps> = ({
  user,
  open,
  onOpenChange,
  onConfirm,
  isDeleting = false,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const { t } = useTranslation();

  if (!user) return null;

  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix"
        >
          <SheetHeader className={`${isTablet ? 'p-6 border-b flex-shrink-0' : 'p-6 border-b flex-shrink-0 pt-[calc(1.5rem+env(safe-area-inset-top))] pwa-safe-header'}`}>
            <SheetTitle>{t('users.deleteUser')}</SheetTitle>
            <SheetDescription
              dangerouslySetInnerHTML={{
                __html: t('users.confirmDeleteMessage', { userName: user.full_name })
              }}
            />
          </SheetHeader>
          <div className={`${isTablet ? 'flex-1 p-6 flex flex-col justify-center' : 'flex-1 p-6 flex flex-col justify-end'}`}>
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isDeleting}
                className="w-full"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={onConfirm}
                disabled={isDeleting}
                className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? t('users.deleting') : t('users.deleteUser')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('users.deleteUser')}</AlertDialogTitle>
          <AlertDialogDescription
            dangerouslySetInnerHTML={{
              __html: t('users.confirmDeleteMessage', { userName: user.full_name })
            }}
          />
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? t('users.deleting') : t('users.deleteUser')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default UserDeleteDialog;
